import { useState, useEffect } from 'react';
import { X, Search, UserCheck, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import type { EmployeeRow } from '../lib/database.types';

interface Props {
  onClose: () => void;
  onClaimed: (profile: EmployeeRow) => void;
}

const ClaimProfileModal = ({ onClose, onClaimed }: Props) => {
  const { claimProfile } = useAuth();
  const [profiles, setProfiles] = useState<EmployeeRow[]>([]);
  const [filtered, setFiltered] = useState<EmployeeRow[]>([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<EmployeeRow | null>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [isClaiming, setIsClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('employees')
      .select('*')
      .is('user_id', null)
      .order('name')
      .then(({ data }) => {
        const rows = data ?? [];
        setProfiles(rows);
        setFiltered(rows);
        setIsFetching(false);
      });
  }, []);

  useEffect(() => {
    const q = search.toLowerCase().trim();
    if (!q) {
      setFiltered(profiles);
    } else {
      setFiltered(profiles.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.role.toLowerCase().includes(q) ||
        p.department.toLowerCase().includes(q)
      ));
    }
    setSelected(null);
  }, [search, profiles]);

  const handleClaim = async () => {
    if (!selected) return;
    setIsClaiming(true);
    setError(null);
    const { error } = await claimProfile(selected.id);
    if (error) {
      setError(error);
      setIsClaiming(false);
    } else {
      onClaimed(selected);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-bolt-surface border border-bolt-border rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh] animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-bolt-border flex-shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-bolt-text">Link an existing profile</h2>
            <p className="text-xs text-bolt-textMuted mt-0.5">Select the profile that belongs to you</p>
          </div>
          <button
            onClick={onClose}
            className="text-bolt-textSubtle hover:text-bolt-textMuted transition-colors p-1 rounded-lg hover:bg-bolt-surfaceHover"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-bolt-border flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name, role, or department..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="form-input pl-9 text-xs"
              autoFocus
            />
          </div>
        </div>

        {/* Profile list */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2 min-h-0">
          {isFetching ? (
            <div className="flex items-center justify-center py-10">
              <Loader className="h-5 w-5 text-bolt-textSubtle animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-bolt-textSubtle text-center py-8">
              {search ? 'No profiles match your search.' : 'All profiles have been claimed.'}
            </p>
          ) : (
            filtered.map(profile => (
              <button
                key={profile.id}
                onClick={() => setSelected(s => s?.id === profile.id ? null : profile)}
                className={`w-full text-left rounded-lg border p-3 flex items-center gap-3 transition-all ${
                  selected?.id === profile.id
                    ? 'border-primary-500 bg-primary-600/10'
                    : 'border-bolt-border bg-bolt-bg hover:border-bolt-borderHover hover:bg-bolt-surfaceHover'
                }`}
              >
                {/* Avatar */}
                <div className="w-9 h-9 rounded-full flex-shrink-0 overflow-hidden bg-bolt-surfaceHover border border-bolt-border">
                  {profile.avatar_url ? (
                    <img src={profile.avatar_url} alt={profile.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs font-semibold text-bolt-textMuted">
                      {profile.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-bolt-text truncate">{profile.name}</p>
                  <p className="text-xs text-bolt-textMuted truncate">{profile.role}</p>
                  <p className="text-xs text-bolt-textSubtle">{profile.department}{[profile.city, profile.country].filter(Boolean).length > 0 ? ` · ${[profile.city, profile.country].filter(Boolean).join(', ')}` : ''}</p>
                </div>

                {/* Check indicator */}
                {selected?.id === profile.id && (
                  <UserCheck className="h-4 w-4 text-primary-400 flex-shrink-0" />
                )}
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-bolt-border flex-shrink-0 space-y-3">
          {error && (
            <p className="text-xs text-error-400 bg-error-500/10 border border-error-500/20 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          <div className="flex items-center justify-end gap-3">
            <button onClick={onClose} className="btn btn-secondary text-xs px-4 py-2">
              Cancel
            </button>
            <button
              onClick={handleClaim}
              disabled={!selected || isClaiming}
              className="btn btn-primary text-xs px-4 py-2 gap-1.5"
            >
              <UserCheck className="h-3.5 w-3.5" />
              {isClaiming ? 'Linking...' : selected ? `Link "${selected.name}"` : 'Select a profile'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimProfileModal;
